import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { getAuthHeaders } from "../../../../utils/auth";
import { buildDateRangeParams } from "../utils/dateRange";

const TAB_DATA_KEY = {
  revenue: "dashboardData",
  popular: "dashboardData",
  costs: "costData",
  locations: "locationData",
  safe: "safeData",
  payouts: "payoutData",
};

const DATA_KEY_ENDPOINT = {
  dashboardData: "/statistics/dashboard",
  costData: "/costs/calculate",
  locationData: "/costs/by-location",
  safeData: "/costs/safe-reconciliation",
  payoutData: "/costs/trainer-payouts",
};

const EMPTY_DATA = { dashboardData: null, costData: null, locationData: null, safeData: null, payoutData: null };

export const useStatisticsData = ({ appliedDateFrom, appliedDateTo, activeTabKey }) => {
  const [data, setData] = useState(EMPTY_DATA);
  const [loadingKey, setLoadingKey] = useState(null);
  const [error, setError] = useState("");
  const paramsSigRef = useRef(`${appliedDateFrom}|${appliedDateTo}`);

  const fetchKey = useCallback(
    async (dataKey) => {
      setLoadingKey(dataKey);
      setError("");
      try {
        const headers = await getAuthHeaders();
        const params = buildDateRangeParams(appliedDateFrom, appliedDateTo);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_ADDRESS}${DATA_KEY_ENDPOINT[dataKey]}?${params.toString()}`,
          { headers }
        );
        if (response.status === 200) {
          setData((prev) => ({ ...prev, [dataKey]: response.data }));
        }
      } catch (err) {
        console.error("Failed to fetch statistics", err);
        if (err.response && (err.response.status === 403 || err.response.status === 401)) {
          setError("Brak uprawnień do pobrania statystyk");
        } else {
          setError("Nie udało się załadować statystyk");
        }
      } finally {
        setLoadingKey(null);
      }
    },
    [appliedDateFrom, appliedDateTo]
  );

  // Invalidate all cached data whenever the applied date range changes
  useEffect(() => {
    const sig = `${appliedDateFrom}|${appliedDateTo}`;
    if (sig !== paramsSigRef.current) {
      paramsSigRef.current = sig;
      setData(EMPTY_DATA);
    }
  }, [appliedDateFrom, appliedDateTo]);

  const activeDataKey = TAB_DATA_KEY[activeTabKey];

  // Lazily fetch whichever data key the active tab needs, only if not cached
  useEffect(() => {
    if (activeDataKey && data[activeDataKey] === null) {
      fetchKey(activeDataKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDataKey, data[activeDataKey], fetchKey]);

  const forceRefetchActive = useCallback(() => {
    if (activeDataKey) fetchKey(activeDataKey);
  }, [activeDataKey, fetchKey]);

  return {
    dashboardData: data.dashboardData,
    costData: data.costData,
    locationData: data.locationData,
    safeData: data.safeData,
    payoutData: data.payoutData,
    loading: loadingKey !== null,
    error,
    forceRefetchActive,
  };
};
